import React from "react";
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow
} from "@coreui/react-pro";
import { env } from "../../Utils/Environment";
import { useSeo } from "../../hooks/useSeo";

interface TermsAndConditionsProps { }

export const TermsAndConditions: React.FC<TermsAndConditionsProps> = () => {
  useSeo({
    title: "Términos y condiciones | DomicilApp",
    description: "Condiciones de uso de DomicilApp para clientes, negocios y domiciliarios: pedidos, cancelaciones, pagos contra entrega y uso de la ubicación.",
  });

  return (
    <div className="terms-conditions-page bg-light py-5">
      <CContainer style={{ maxWidth: "960px" }}>

        {/* ENCABEZADO DE LA PÁGINA */}
        <div className="text-center mb-5">
          <CBadge
            color="danger"
            className="px-3 py-2 text-uppercase tracking-wider fw-bold mb-3 shadow-sm"
            style={{ backgroundColor: "#FE2445" }}
          >
            Marco Legal & Acuerdos de Uso
          </CBadge>
          <h1 className="fw-black display-5 text-dark mb-2">
            Términos y Condiciones
          </h1>
          <p className="text-muted fs-6 mb-0">
            Última actualización: <strong>24 de octubre de 2026</strong>
          </p>
        </div>

        {/* CONTENEDOR PRINCIPAL */}
        <CCard className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">

          {/* INTRODUCCIÓN */}
          <CCardHeader
            className="py-4 px-4 px-md-5 border-0 text-white"
            style={{ backgroundColor: "#19202C" }}
          >
            <p className="fs-5 mb-0 lh-base text-white-50">
              Los presentes Términos y Condiciones rigen el acceso y uso de la plataforma digital, aplicaciones y servicios integrados de <strong className="text-white">DOMICILAPP</strong> (en adelante, la "Plataforma"), que regulan la relación entre DomicilApp, los Comercios Aliados y los Usuarios Finales.
            </p>
          </CCardHeader>

          <CCardBody className="p-4 p-md-5 text-secondary">
            <CRow className="g-4">

              {/* 1. ACEPTACIÓN */}
              <CCol md={12}>
                <div className="p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">1. Aceptación de los Términos</h4>
                  <p className="mb-0 text-secondary">
                    El acceso, registro y uso de DomicilApp por parte de usuarios o comercios implica la aceptación plena e incondicional de los presentes Términos y Condiciones. Si no está de acuerdo con las condiciones establecidas, deberá abstenerse de utilizar la Plataforma.
                  </p>
                </div>
              </CCol>

              {/* 2. DESCRIPCIÓN DEL SERVICIO Y MODALIDADES */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">2. Naturaleza del Servicio</h4>
                  <p className="small text-secondary mb-0">
                    DomicilApp es una plataforma digital que intermedia entre los comercios locales y los clientes finales, facilitando la recepción de pedidos en modalidades de <strong>Entrega a Domicilio</strong> y <strong>Pickup (Pide y Recoge en tienda)</strong>.
                  </p>
                </div>
              </CCol>

              {/* 3. REGISTRO Y MANTENIMIENTO DE CUENTAS */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">3. Registro de Cuenta</h4>
                  <p className="small text-secondary mb-0">
                    La activación del servicio para comercios y usuarios se realiza mediante el formulario oficial en <a href={`${env('PORTAL_URL')}/signup`} className="fw-semibold text-decoration-none" style={{ color: "#FE2445" }}>nuestro portal de registro</a>. El usuario o comercio es responsable de la veracidad y custodia de sus credenciales.
                  </p>
                </div>
              </CCol>

              {/* 4. PRECIOS Y TARIFAS DE SERVICIO */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">4. Tarifas y Saldos Promocionales</h4>
                  <p className="small text-secondary mb-0">
                    DomicilApp ofrece esquemas de registro gratuito e incentivos iniciales. El valor de los servicios digitales adicionales (alertas SMS/WhatsApp, visibilidad destacada, cupones) se rige según las tarifas vigentes publicadas en la plataforma y pueden modificarse con previo aviso. DomicilApp puede cobrar una comisión sobre las transacciones realizadas a través de la plataforma.
                  </p>
                </div>
              </CCol>

              {/* 5. RESPONSABILIDAD DE LOS PRODUCTOS (NUEVO / ADICIONAL) */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">5. Calidad y Estado de Productos</h4>
                  <p className="small text-secondary mb-0">
                    Cada comercio aliado es el único y exclusivo responsable de la preparación, empaque, vigencia, estado sanitario e idoneidad de los productos ofertados en la app (alimentos, medicinas, abarrotes, etc.).
                  </p>
                </div>
              </CCol>

              {/* 6. CANCELACIONES Y DEVOLUCIONES (NUEVO / ADICIONAL) */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-2">6. Políticas de Cancelación y Pedidos</h4>
                  <p className="small text-secondary mb-0">
                    Un pedido en estado <em>"En Preparación"</em> o <em>"En Camino"</em> no podrá ser cancelado sin la aceptación expresa del comercio involucrado. En caso de inconsistencias o falta de disponibilidad, la Plataforma o el Comercio contactarán al usuario para reprogramar o anular la solicitud sin recargo.
                  </p>
                </div>
              </CCol>

              {/* 7. OBLIGACIONES DEL CLIENTE Y COMERCIOS */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">7. Obligaciones del Usuario</h4>
                  <p className="small text-secondary mb-0">
                    Suministrar información exacta de entrega, utilizar la app acorde con las leyes colombianas y abstenerse de realizar solicitudes fraudulentas o malintencionadas que afecten a los comercios o domiciliarios. Está prohibido usar la app para fines ilegales, enviar contenido ofensivo o manipular precios.
                  </p>
                </div>
              </CCol>

              {/* 8. PROPIEDAD INTELECTUAL */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">8. Propiedad Intelectual</h4>
                  <p className="small text-secondary mb-0">
                    Los logotipos, software, bases de datos y marcas relativas a DomicilApp son propiedad exclusiva de la empresa. Queda prohibida la reproducción parcial o total no autorizada de sus componentes.
                  </p>
                </div>
              </CCol>

              {/* 9. TERMINACIÓN Y CESIÓN */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">9. Terminación y Cesión</h4>
                  <p className="small text-secondary mb-0">
                    El contrato o acuerdo de uso puede finalizar por incumplimiento grave de las partes o por desvinculación voluntaria de la cuenta. DomicilApp se reserva la facultad de ceder la operación previa notificación y de modificar estos términos, notificando los cambios a través de la app o por correo electrónico.
                  </p>
                </div>
              </CCol>

              {/* 10. PROTECCIÓN DE DATOS Y CONFIDENCIALIDAD */}
              <CCol md={6}>
                <div className="h-100 p-4 rounded-3 border border-secondary border-opacity-10 bg-white">
                  <h4 className="fw-bold text-dark mb-2">10. Protección de Datos</h4>
                  <p className="small text-secondary mb-0">
                    El tratamiento de datos personales de usuarios y aliados se efectúa bajo la normativa legal vigente de protección de datos (Habeas Data, Ley 1581 de 2012) y en estricto cumplimiento de nuestra Política de Privacidad.
                  </p>
                </div>
              </CCol>

              {/* 11. DOMICILIARIOS Y USO DE LA UBICACIÓN */}
              <CCol md={12}>
                <div className="p-4 rounded-3 bg-light border border-secondary border-opacity-10">
                  <h4 className="fw-bold text-dark mb-2">11. Domiciliarios y Uso de la Ubicación</h4>
                  <p className="small text-secondary mb-2">
                    Al activar el modo domiciliario, el repartidor autoriza a DomicilApp a recopilar su ubicación mientras esté en turno, incluso con la app cerrada o en segundo plano, para ofrecerle pedidos cercanos y mostrar el avance de la entrega al cliente, al comercio y, si aplica, a su empresa de domicilios.
                  </p>
                  <p className="small text-secondary mb-2">
                    Mientras tenga un pedido en curso, desde que lo acepta hasta que lo entrega, se guarda el historial de su recorrido. Este historial sirve como soporte para resolver reclamos sobre el pedido y para calcular tiempos y distancias reales de entrega, y se elimina automáticamente a los <strong>90 días</strong>.
                  </p>
                  <p className="small text-secondary mb-0">
                    El repartidor puede dejar de compartir su ubicación cambiando de rol, cerrando sesión o retirando el permiso en su teléfono; sin ese permiso no podrá recibir pedidos. El detalle del tratamiento está en nuestra <a href="/policyprivacy" className="fw-semibold text-decoration-none" style={{ color: "#FE2445" }}>Política de Privacidad</a>.
                  </p>
                </div>
              </CCol>

              {/* 12. LEY APLICABLE Y JURISDICCIÓN (ADAPTADO A COLOMBIA) */}
              <CCol md={12}>
                <div className="p-4 rounded-4 text-white shadow-sm" style={{ backgroundColor: "#19202C" }}>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="fs-4">⚖️</span>
                    <h4 className="fw-bold text-white mb-0">12. Ley Aplicable y Jurisdicción</h4>
                  </div>
                  <p className="text-white-50 small mb-0">
                    Los presentes Términos y Condiciones se rigen e interpretan de conformidad con la legislación aplicable de la <strong>República de Colombia</strong>. Para cualquier controversia derivada del presente documento, las partes se someten a la competencia de los tribunales de la República de Colombia.
                  </p>
                </div>
              </CCol>

            </CRow>
          </CCardBody>
        </CCard>

      </CContainer>
    </div>
  );
};